import { Capacitor } from '@capacitor/core';
import { getAuth } from 'firebase/auth';
import { doc, getFirestore, updateDoc } from 'firebase/firestore';

let store: any = null;

export class PurchaseService {
  private static instance: PurchaseService;
  private initialized = false;

  private constructor() {}

  static getInstance(): PurchaseService {
    if (!PurchaseService.instance) {
      PurchaseService.instance = new PurchaseService();
    }
    return PurchaseService.instance;
  }

  async initialize() {
    if (this.initialized) return;

    if (Capacitor.getPlatform() === 'ios') {
      return new Promise<void>((resolve, reject) => {
        let attempts = 0;
        const maxAttempts = 10;
        const checkInterval = 1000; // 1 second

        const checkStore = () => {
          attempts++;
          console.log(`[StoreKit] Checking store (attempt ${attempts}/${maxAttempts})`);
          
          const storeTemp = (window as any).store;
          
          if (storeTemp && typeof storeTemp.register === 'function') {
            console.log('[StoreKit] Store found with methods:', Object.keys(storeTemp));
            store = storeTemp;
            
            // Basic initialization
            try {
              store.verbosity = store.DEBUG;
              
              // Register the product
              store.register({
                id: 'syb_monthly_001',
                type: store.PAID_SUBSCRIPTION,
                platform: store.PLATFORM_IOS
              });

              // Set up handlers
              store.when('syb_monthly_001')
                .approved((p: any) => {
                  console.log('[StoreKit] Product approved');
                  p.finish();
                  this.updateUserPremiumStatus(true);
                })
                .owned(() => {
                  console.log('[StoreKit] Product owned');
                  this.updateUserPremiumStatus(true);
                })
                .expired(() => {
                  console.log('[StoreKit] Product expired');
                  this.updateUserPremiumStatus(false);
                })
                .error((e: any) => {
                  console.error('[StoreKit] Product error:', e);
                });

              // Initialize the store
              store.ready(() => {
                console.log('[StoreKit] Store ready');
                store.refresh();
              });

              this.initialized = true;
              resolve();
              return;
            } catch (e) {
              console.error('[StoreKit] Error during initialization:', e);
            }
          }

          if (attempts < maxAttempts) {
            console.log('[StoreKit] Store not ready, retrying...');
            setTimeout(checkStore, checkInterval);
          } else {
            const error = new Error('Store initialization failed after maximum attempts');
            console.error('[StoreKit]', error);
            reject(error);
          }
        };

        // Start checking after deviceready
        if (document.readyState === 'complete') {
          checkStore();
        } else {
          document.addEventListener('deviceready', checkStore, false);
        }
      });
    } else {
      this.initialized = true;
    }
  }

  public async purchasePremium(): Promise<boolean> {
    if (Capacitor.getPlatform() === 'ios') {
      try {
        await this.initialize();

        const products = store?.products || [];
        const premiumProduct = products.find((p: { id: string }) => p.id === 'syb_monthly_001');
        if (!premiumProduct) throw new Error('Premium subscription product not found');

        console.log('[StoreKit] Products:', store.products);
        store.order('syb_monthly_001');

        return new Promise((resolve, reject) => {
          const timeout = setTimeout(() => reject(new Error('Purchase timeout')), 60000);

          store.when('syb_monthly_001').approved(() => {
            clearTimeout(timeout);
            resolve(true);
          });

          store.when('syb_monthly_001').error((err: any) => {
            clearTimeout(timeout);
            console.error('Store error:', err);
            reject(new Error(err?.message || 'Purchase failed'));
          });
        });
      } catch (error) {
        console.error('Purchase failed:', error);
        throw error;
      }
    } else {
      return this.existingStripePurchase();
    }
  }

  private async updateUserPremiumStatus(isPremium: boolean): Promise<boolean> {
    try {
      const response = await fetch('/api/update-premium-status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isPremium })
      });

      if (!response.ok) throw new Error('Failed to update premium status');
      return true;
    } catch (error) {
      console.error('Failed to update premium status:', error);
      return false;
    }
  }

  private async existingStripePurchase(): Promise<boolean> {
    try {
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) throw new Error('Failed to create checkout session');

      const { url } = await response.json();
      window.location.href = url;
      return true;
    } catch (error) {
      console.error('Failed to start checkout:', error);
      throw error;
    }
  }
}
